import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { articles } from "../articals/articles";
import ContactForm from "../Component/ContactForm";
import { Helmet } from 'react-helmet-async';

const categoryMap = {
    AI: { color: "text-blue-600", bg: "bg-blue-500/10", icon: "memory", label: "Intelligence Report" },
    Story: { color: "text-orange-600", bg: "bg-orange-500/10", icon: "auto_stories", label: "Narrative Log" },
    Travel: { color: "text-green-600", bg: "bg-green-500/10", icon: "explore", label: "Field Expedition" },
    Products: { color: "text-indigo-600", bg: "bg-indigo-500/10", icon: "inventory_2", label: "System Architecture" },
    Default: { color: "text-primary", bg: "bg-primary/10", icon: "article", label: "General Document" }
};

const BlogDetailsPage = () => {
    const { slug } = useParams();
    const article = articles.find((a) => (a.slug || a.id.toString()) === slug);

    const theme = useMemo(() => {
        if (!article) return categoryMap.Default;
        const primaryTag = article?.category?.[0];
        return categoryMap[primaryTag] || categoryMap.Default;
    }, [article]);

    const relatedBlogs = useMemo(() => {
        if (!article) return [];
        return articles
            .filter(a => a.id !== article.id && a.category?.includes(article.category?.[0]))
            .slice(0, 3);
    }, [article]);

    const latestBlogs = useMemo(() => {
        return [...articles].sort((a, b) => b.id - a.id).slice(0, 3);
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [slug]);

    if (!article) {
        return (
            <div className="pt-40 text-center font-display font-bold text-2xl h-screen">
                DOC_MISSING_0x404
            </div>
        );
    }
    return (
        <>
            <Helmet>
                <title>{`${article.title} | Rahul Biswas Blog`}</title>
                <meta name="description" content={article.excerpt} />
                <meta name="keywords" content={article.tags.join(', ')} />
                <meta property="og:title" content={article.title} />
                <meta property="og:description" content={article.excerpt} />
                <meta property="og:type" content="article" />
            </Helmet>
            <main className="min-h-screen bg-[#e8e6e1] dark:bg-[#050608] pt-28 transition-colors duration-700">
                <div className="w-[90%] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10 md:gap-14 items-start mb-20">

                    {/* ================= DYNAMIC TECHNICAL DOSSIER ================= */}
                    <motion.article
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                        className="relative bg-white dark:bg-[#11141b] border border-[#e2e8f0] dark:border-white/10 shadow-xl overflow-hidden font-sans"
                    >
                        {/* Folder Tab */}
                        <div className="absolute top-0 right-0 w-24 md:w-32 h-6 md:h-8 bg-[#f1f5f9] dark:bg-white/5 border-l border-b border-[#e2e8f0] dark:border-white/10 flex items-center justify-center">
                            <span className="text-[8px] md:text-[9px] font-mono font-bold text-gray-400 uppercase tracking-tighter">
                                FILE_ID: {article.id.toString().padStart(4, '0')}
                            </span>
                        </div>

                        {/* Engineering Header */}
                        <header className="p-6 md:p-12 border-b border-[#e2e8f0] dark:border-white/10 bg-[#f8fafc]/50 dark:bg-white/[0.02]">
                            <div className="flex flex-col gap-6">
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className={`px-2.5 py-1 ${theme.bg} ${theme.color} text-[10px] font-bold uppercase tracking-widest rounded-md border ${theme.color.replace('text', 'border')}/20`}>
                                        {theme.label}
                                    </span>
                                    <span className="hidden sm:block h-4 w-px bg-gray-200 dark:bg-white/10"></span>
                                    <div className="flex items-center gap-2">
                                        <span className="relative flex h-2 w-2">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                        </span>
                                        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">Active System Log</span>
                                    </div>
                                </div>

                                <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-slate-900 dark:text-white leading-tight tracking-tight max-w-4xl italic">
                                    {article.title}
                                </h1>

                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-dashed border-gray-200 dark:border-white/10">
                                    <div className="flex flex-col gap-1">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Lead Developer</p>
                                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 italic">Rahul Biswas</p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Affiliation</p>
                                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 italic text-[12px]">IIT Patna // AI & ML</p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Subject Tag</p>
                                        <p className={`text-sm font-semibold ${theme.color}`}>{article.tags?.[0]}</p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Revision</p>
                                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{article.publishedDate}</p>
                                    </div>
                                </div>
                            </div>
                        </header>

                        {/* Section Rendering */}
                        <div className="flex flex-col">
                            <div className="flex-grow p-6 md:p-12 border-r border-[#e2e8f0] dark:border-white/10 space-y-8 md:space-y-10">
                                {article.sections?.map((section, idx) => {
                                    switch (section.type) {
                                        case "callout":
                                            return (
                                                <div key={idx} className="relative p-6 md:p-8 bg-slate-50 dark:bg-white/[0.03] border-l-4 border-primary rounded-r-xl">
                                                    <span className="absolute top-0 left-0 px-2 py-1 bg-primary text-white text-[9px] font-mono uppercase tracking-[0.2em]">{section.label || "Abstract"}</span>
                                                    <p className="text-base md:text-lg font-medium text-slate-800 dark:text-slate-200 italic leading-relaxed m-0">"{section.content}"</p>
                                                </div>
                                            );
                                        case "heading":
                                            return <h3 key={idx} className="text-lg md:text-xl font-bold pt-4 text-slate-900 dark:text-white">{section.content}</h3>;
                                        case "paragraph":
                                            return <p key={idx} className="text-sm md:text-base leading-relaxed text-slate-600 dark:text-slate-400">{section.content}</p>;
                                        case "stats":
                                            return (
                                                <div key={idx} className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-${section.gridCols || 3} gap-4 my-8`}>
                                                    {section.data?.map((stat, sIdx) => (
                                                        <div key={sIdx} className="p-4 bg-[#f8fafc] dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-center">
                                                            <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">{stat.label}</p>
                                                            <p className="text-xl md:text-2xl font-black text-primary">{stat.value}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        case "code":
                                            return (
                                                <div key={idx} className="my-6 md:my-8 rounded-lg overflow-hidden border border-slate-800 bg-[#0f172a] shadow-lg">
                                                    <div className="flex justify-between items-center px-4 py-2 bg-slate-800/50 border-b border-slate-700">
                                                        <span className="text-[10px] font-mono text-slate-400">{section.label}</span>
                                                        <span className="text-[9px] font-mono text-slate-500 uppercase">{section.language}</span>
                                                    </div>
                                                    <pre className="p-4 md:p-6 text-xs md:text-sm font-mono text-slate-300 overflow-x-auto">
                                                        <code>{section.content}</code>
                                                    </pre>
                                                </div>
                                            );
                                        case "image":
                                            return (
                                                <figure key={idx} className={`my-8 md:my-12 group ${section.fullBleed ? '-mx-6 md:-mx-12' : 'rounded-xl border border-[#e2e8f0] dark:border-white/5 p-1.5 md:p-2 bg-white dark:bg-white/5 shadow-sm'}`}>
                                                    <div className={`relative overflow-hidden ${section.fullBleed ? '' : 'rounded-lg'}`}>
                                                        <img src={section.url} alt={section.caption} className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-1000" />
                                                        <div className="absolute inset-0 bg-[radial-gradient(#8080801a_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />
                                                    </div>
                                                    <figcaption className="mt-3 text-[10px] font-mono text-gray-400 text-center uppercase tracking-[0.2em]">{section.caption}</figcaption>
                                                </figure>
                                            );
                                        case "list":
                                            return (
                                                <div key={idx} className="p-4 md:p-6 border border-slate-200 dark:border-white/10 rounded-lg bg-slate-50/50 dark:bg-white/5 my-8">
                                                    <h4 className="text-xs font-black uppercase tracking-widest text-primary mb-4">{section.title}</h4>
                                                    <ul className="space-y-3 m-0 p-0 list-none">
                                                        {section.items?.map((item, iIdx) => (
                                                            <li key={iIdx} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                                                                <span className="material-symbols-outlined text-primary text-sm mt-0.5">verified</span>
                                                                {item}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            );
                                        case "divider":
                                            return <div key={idx} className={`h-px w-full my-8 md:my-12 ${section.style === 'dashed' ? 'border-t border-dashed border-slate-200 dark:border-white/10' : 'bg-gradient-to-r from-transparent via-slate-200 dark:via-white/10 to-transparent'}`} />;
                                        default: return null;
                                    }
                                })}
                            </div>

                            {/* Marginal Intelligence */}
                            <aside className="w-full flex flex-col md:flex-row bg-[#f8fafc]/30 dark:bg-white/[0.01] p-6 md:p-12 gap-8 border-t border-[#e2e8f0] dark:border-white/10">
                                <div className="flex-1">
                                    <h4 className="text-[11px] font-bold text-slate-900 dark:text-white uppercase tracking-widest mb-4 italic">Lead Observation</h4>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed italic border-l-2 border-primary pl-4">
                                        "The intersection of casual curiosity and logical scalability is where true product innovation is born."
                                    </p>
                                </div>
                                <div className="flex-1 pt-6 md:pt-0 border-t md:border-t-0 md:border-l border-dashed border-gray-200 dark:border-white/10 md:pl-8">
                                    <h4 className="text-[11px] font-bold text-slate-900 dark:text-white uppercase tracking-widest mb-4">Registry Details</h4>
                                    <div className="space-y-3 font-mono text-[10px] text-gray-400 uppercase">
                                        <p className="flex justify-between"><span>Lead:</span> <span className="text-slate-700 dark:text-slate-300 font-bold italic text-right">Rahul Biswas</span></p>
                                        <p className="flex justify-between"><span>Hub:</span> <span className="text-slate-700 dark:text-slate-300 font-bold uppercase">IIT_PATNA</span></p>
                                        <p className="flex justify-between"><span>Status:</span> <span className="text-green-600 font-bold">Verified Log</span></p>
                                    </div>
                                </div>
                                <div className="flex-1 p-5 bg-primary/5 rounded-xl border border-primary/10">
                                    <p className="text-[10px] font-bold text-primary uppercase mb-2 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-sm">psychology</span> Cognitive State
                                    </p>
                                    <p className="text-[11px] text-slate-600 dark:text-slate-400 italic">
                                        Focuses on Creative Introversion and Self-Motivated Problem Solving logic.
                                    </p>
                                </div>
                            </aside>
                        </div>

                        {/* Dossier Footer */}
                        <footer className="px-6 md:px-8 py-6 bg-[#f8fafc] dark:bg-white/[0.03] border-t border-[#e2e8f0] dark:border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4 font-mono text-[9px] text-gray-400 uppercase">
                            <div className="flex items-center gap-4">
                                <span className="flex items-center gap-1.5 text-green-600 font-bold">
                                    <span className="material-symbols-outlined text-xs">verified</span> Dossier Verified
                                </span>
                                <span>Ref: 0x{article.id}B_SYS</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span>{article.readTime}</span>
                                <span className="hidden sm:inline text-gray-300">|</span>
                                <span>© {new Date().getFullYear()} Rahul Biswas</span>
                            </div>
                        </footer>
                    </motion.article>

                    {/* ================= SIDEBAR ================= */}
                    <aside className="lg:sticky lg:top-24 space-y-8 md:space-y-12">
                        <div>
                            <h4 className={`text-[10px] font-black uppercase mb-6 tracking-widest flex items-center gap-2 ${theme.color}`}>
                                <span className="material-symbols-outlined text-sm">hub</span> Related Logs
                            </h4>
                            <div className="grid grid-cols-1 gap-6">
                                {relatedBlogs.map(blog => (
                                    <Link key={blog.id} to={`/blog/${blog.slug || blog.id}`} className="block group">
                                        <p className="text-[9px] font-mono text-gray-400 mb-1">0x{blog.id}</p>
                                        <h5 className="text-sm font-bold dark:text-white group-hover:text-primary transition">{blog.title}</h5>
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h4 className="text-[10px] font-black uppercase text-gray-400 mb-6 tracking-widest flex items-center gap-2">
                                <span className="material-symbols-outlined text-sm">history_edu</span> Latest Archives
                            </h4>
                            <div className="grid grid-cols-1 gap-6">
                                {latestBlogs.map(blog => (
                                    <Link key={blog.id} to={`/blog/${blog.slug || blog.id}`} className="flex gap-4 group items-center">
                                        <div className="size-12 bg-white dark:bg-white/10 border border-[#dcdad5] overflow-hidden rounded-lg">
                                            <img src={blog.thumbnail} alt="" className="w-full h-full object-cover group-hover:scale-110 transition duration-500" />
                                        </div>
                                        <div>
                                            <h5 className="text-[11px] font-bold dark:text-white group-hover:text-primary transition line-clamp-2">{blog.title}</h5>
                                            <p className="text-[9px] font-mono text-gray-400 mt-1 uppercase">{blog.publishedDate}</p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <Link to="/blog" className="block text-center py-5 border-t border-dashed border-[#dcdad5] dark:border-white/10 text-[10px] font-black uppercase text-primary tracking-[0.3em]">
                            End of Index
                        </Link>
                    </aside>
                </div>

                {/* --- Contact Section --- */}
                <section id="contact" className="relative py-32 bg-[#0d121b] dark:bg-black text-white overflow-hidden">
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4 -z-0"></div>
                    <div className="w-[90%] mx-auto relative z-10">
                        <div className="grid lg:grid-cols-2 gap-16 items-start">
                            <div className="space-y-8">
                                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase font-display">
                                    <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                                    </span> Let's Connect
                                </div>
                                <h2 className="text-5xl md:text-7xl font-bold font-display leading-[1.1] tracking-tight">
                                    Let's build something <span className="text-primary">legendary.</span>
                                </h2>
                                <div className="space-y-6 pt-4">
                                    <a className="flex items-center gap-5 text-xl font-medium hover:text-primary transition-all group font-display" href="mailto:rahul@contacthq.in">
                                        <span className="size-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-primary/20 transition-all">
                                            <span className="material-symbols-outlined text-primary text-3xl">mail</span>
                                        </span> rahul@contacthq.in
                                    </a>
                                    {/* Socials (Simplified) */}
                                    <div className="flex flex-wrap gap-4 pt-4">
                                        <a href="https://www.linkedin.com/in/rahulbiswas0675/" className="size-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#0077b5] transition-all"><i className="devicon-linkedin-plain text-2xl"></i></a>
                                        {/* Add other social links as needed */}
                                    </div>
                                </div>
                            </div>
                            <div className="relative">
                                <ContactForm />
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
};

export default BlogDetailsPage;